import React, { useMemo, useState } from 'react';
import { Spin } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { RoomOptionsListProps } from '../types/bot.types';
import HotelHeaderCard from './HotelHeaderCard';
import RoomOptionCard from './RoomOptionCard';
import CollapsibleResultGroup from './CollapsibleResultGroup';
import { formatBotPrice } from './formatBotPrice';
import { getHotelRoomOptionDetails } from './formatHotelRoomOption';

/** Ratehawk-style board codes → human labels. Unknown codes are title-cased. */
const BOARD_LABELS: Record<string, string> = {
  nomeal: 'No meal',
  breakfast: 'Breakfast',
  'breakfast-buffet': 'Breakfast buffet',
  halfboard: 'Half board',
  'half-board': 'Half board',
  fullboard: 'Full board',
  'full-board': 'Full board',
  allinclusive: 'All inclusive',
  'all-inclusive': 'All inclusive',
};

function prettifyBoard(board?: string): string | undefined {
  if (!board) return undefined;
  const key = board.toLowerCase().trim();
  if (BOARD_LABELS[key]) return BOARD_LABELS[key];
  // Fallback: "some_code" / "some-code" → "Some code"
  const spaced = key.replace(/[-_]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Whole nights between two YYYY-MM-DD dates; undefined if unparseable. */
function nightsBetween(start?: string, end?: string): number | undefined {
  if (!start || !end) return undefined;
  const a = Date.parse(start);
  const b = Date.parse(end);
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return undefined;
  return Math.round((b - a) / 86_400_000);
}

const RoomOptionsList: React.FC<RoomOptionsListProps> = ({
  raw,
  onSelect,
  disabled = false,
  selectedKey,
  pickLabel,
}) => {
  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const [selectingHash, setSelectingHash] = useState<string | null>(null);

  const pricing = raw?.pricing ?? {};
  const hotelInfo = raw?.hotelInfo ?? {};
  // hotelInfo is the raw search-result product → fields nest under `.info`.
  // Probe nested first, then flat, so a future getHotelInfo success path works too.
  const info = hotelInfo.info ?? hotelInfo;

  const currency: string =
    pricing.currency_sign ?? info.currency_sign ?? info.currency ?? '';
  const nights = nightsBetween(pricing?.date_range?.start, pricing?.date_range?.end);

  // room_id → first image URL, for the per-card image (rooms[].images[0]).
  const roomImageById = useMemo(() => {
    const map = new Map<number, string>();
    const rooms: any[] = Array.isArray(pricing.rooms) ? pricing.rooms : [];
    for (const r of rooms) {
      const img = Array.isArray(r?.images) ? r.images[0] : undefined;
      if (r?.id != null && img) map.set(r.id, img);
    }
    return map;
  }, [pricing.rooms]);

  const hotelHeroImage: string | undefined = Array.isArray(info.images)
    ? info.images[0]
    : undefined;

  const prices: any[] = Array.isArray(pricing.price) ? pricing.price : [];
  if (!prices.length) return null;

  const handleSelect = (price: any) => {
    if (disabled) return;
    const bookHash: string = price?.book_hash;
    if (!bookHash) return;
    setSelectingHash(bookHash);
    const board = prettifyBoard(price?.board);
    const total = formatBotPrice(price?.room_price ?? price?.total_price ?? 0);
    // Friendly bubble text — the exact book_hash rides structurally (see BotWidget).
    const parts = [price?.room_name, board, `${currency}${total}`].filter(Boolean);
    const friendly = `${t?.room?.bookLeadIn ?? "I'll book the"} ${parts.join(' · ')}`;
    const label = price?.room_name ?? price?.room_type ?? board ?? 'Room';
    onSelect(bookHash, friendly, label);
  };

  const list = (
    <div className="bot-search-results bot-room-options">
      <HotelHeaderCard
        name={info.name ?? info.hotel_name ?? ''}
        stars={info.stars ?? info.hotel_stars}
        destination={info.address ?? info.destination ?? info.city}
        imageUrl={hotelHeroImage}
        amenities={Array.isArray(info.amenities) ? info.amenities : undefined}
      />

      <div className="bot-search-results__list">
        {prices.map((price: any, i: number) => {
          const bookHash: string = price?.book_hash ?? `idx-${i}`;
          const isSelecting = selectingHash === price?.book_hash;
          const isPicked = disabled && selectedKey === price?.book_hash;
          const roomImage =
            (price?.room_id != null && roomImageById.get(price.room_id)) ||
            hotelHeroImage;
          const optionDetails = getHotelRoomOptionDetails(price);
          return (
            <div
              key={bookHash}
              className={`bot-result-card-wrapper${
                isSelecting ? ' bot-result-card-wrapper--selecting' : ''
              }${isPicked ? ' bot-result-card-wrapper--picked' : ''}`}
            >
              {isPicked && (
                <span className="bot-result-card-wrapper__pick-badge">
                  {t?.collapsed?.yourPick ?? 'Your pick'}
                </span>
              )}
              <RoomOptionCard
                bookHash={bookHash}
                roomName={price?.room_name ?? price?.room_type ?? ''}
                board={prettifyBoard(price?.board)}
                totalPrice={price?.room_price ?? price?.total_price ?? 0}
                pricePerNight={
                  nights && nights > 0
                    ? (price?.room_price ?? price?.total_price ?? 0) / nights
                    : undefined
                }
                currency={currency}
                freeCancellation={optionDetails.isRefundable}
                guests={optionDetails.guests || undefined}
                capacity={
                  typeof price?.capacity === 'number' && price.capacity > 0
                    ? price.capacity
                    : undefined
                }
                cancellationSummary={optionDetails.cancellationSummary || undefined}
                amenityKeys={optionDetails.amenityKeys}
                imageUrl={roomImage || undefined}
                onSelect={() => handleSelect(price)}
              />
              {isSelecting && (
                <div
                  className="bot-result-card-wrapper__overlay"
                  role="status"
                  aria-live="polite"
                >
                  <Spin size="small" />
                  <span className="bot-result-card-wrapper__overlay-label">
                    {t?.addingToCart ?? 'Adding to cart…'}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  if (!disabled) return list;

  return (
    <CollapsibleResultGroup
      icon="🛏️"
      title={t?.collapsed?.title?.room ?? 'Room options'}
      count={prices.length}
      pickLabel={pickLabel}
    >
      {list}
    </CollapsibleResultGroup>
  );
};

export default RoomOptionsList;
